import { useEffect, useMemo, useState } from "react";
import { Search, Newspaper } from "lucide-react";
import { API } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import Reveal from "@/components/Reveal";

const Articulos = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState(null);

  useEffect(() => {
    API.get("/articles", { params: { category: "articulo", limit: 50 } })
      .then(({ data }) => setArticles(data))
      .finally(() => setLoading(false));
  }, []);

  const tags = useMemo(
    () => [...new Set(articles.flatMap((a) => a.tags || []))].slice(0, 8),
    [articles]
  );

  const filtered = articles.filter((a) => {
    const okTag = !tag || (a.tags || []).includes(tag);
    const q = search.trim().toLowerCase();
    const okSearch =
      !q || a.title.toLowerCase().includes(q) || (a.excerpt || "").toLowerCase().includes(q);
    return okTag && okSearch;
  });

  return (
    <div className="bg-ink pt-32">
      <div className="mx-auto max-w-7xl px-5 pb-24 md:px-12">
        <Reveal>
          <p className="eyebrow mb-3">Opinión, análisis y fútbol en general</p>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl">
            Artículos
          </h1>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                data-testid="articles-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar artículos..."
                className="w-full rounded-full border border-edge bg-ink2 py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 focus:border-crimson focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="articles-tag-filter">
              <button
                data-testid="tag-chip-all"
                onClick={() => setTag(null)}
                className={`rounded-full border px-4 py-1.5 font-mono-score text-[11px] font-semibold uppercase tracking-widest transition-colors ${
                  !tag ? "border-crimson bg-crimson text-white" : "border-edge text-slate-400 hover:text-white"
                }`}
              >
                Todo
              </button>
              {tags.map((t) => (
                <button
                  key={t}
                  data-testid={`tag-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setTag(tag === t ? null : t)}
                  className={`rounded-full border px-4 py-1.5 font-mono-score text-[11px] font-semibold uppercase tracking-widest transition-colors ${
                    tag === t ? "border-crimson bg-crimson text-white" : "border-edge text-slate-400 hover:text-white"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-12" data-testid="articles-grid">
          {loading ? (
            <div className="grid gap-5 md:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-80 animate-pulse rounded-2xl border border-edge bg-ink3" />
              ))}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 3) * 0.08}>
                  <ArticleCard article={a} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-24 text-center">
              <Newspaper className="h-10 w-10 text-slate-600" />
              <p className="text-slate-400">No hay artículos que coincidan con la búsqueda.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Articulos;
